// Extensions
import { History } from './history/index.js';
import { Color } from './color/index.js';
import { FontFamily } from './font-family/index.js';
import { FontSize } from './font-size/index.js';
import { LetterSpacing } from './letter-spacing/index.js';
import { TextAlign } from './text-align/index.js';
import { FormatCommands } from './format-commands/index.js';
import { DropCursor } from './dropcursor/index.js';
import { Gapcursor } from './gapcursor/index.js';
import { Collaboration } from './collaboration/index.js';
import { CollaborationCursor } from './collaboration-cursor/index.js';
import { AiPlugin, AiMark, AiAnimationMark, AiLoaderNode } from './ai/index.js';
import { ContextMenu } from './context-menu';
import {
  StructuredContentCommands,
  StructuredContent,
  StructuredContentBlock,
  DocumentSection,
  DocumentPartObject,
} from './structured-content/index.js';

// Nodes extensions
import { Document } from './document/index.js';
import { Text } from './text/index.js';
import { Run } from './run/index.js';
import { Paragraph } from './paragraph/index.js';
import { Heading } from './heading/index.js';
import { CommentRangeStart, CommentRangeEnd, CommentReference, CommentsMark, CommentsPlugin } from './comment/index.js';
import { FootnoteReference } from './footnote/index.js';
import { EndnoteReference } from './endnote/index.js';
import { TabNode } from './tab/index.js';
import { LineBreak, HardBreak } from './line-break/index.js';
import { NoBreakHyphenNode } from './no-break-hyphen/index.js';
import { Table } from './table/index.js';
import { TableHeader } from './table-header/index.js';
import { TableRow } from './table-row/index.js';
import { TableCell } from './table-cell/index.js';
import { FieldAnnotation, fieldAnnotationHelpers } from './field-annotation/index.js';
import { Image } from './image/index.js';
import { BookmarkStart, BookmarkEnd } from './bookmarks/index.js';
import { SmartTag } from './smart-tag/index.js';
import { Mention } from './mention/index.js';
import { PageNumber, TotalPageCount, SectionPageCount } from './page-number/index.js';
import { PageReference } from './page-reference/index.js';
import { ShapeContainer } from './shape-container/index.js';
import { ShapeTextbox } from './shape-textbox/index.js';
import { ContentBlock } from './content-block/index.js';
import { BlockNode } from './block-node/index.js';
import { TableOfContents, TocPageNumber } from './table-of-contents/index.js';
import { DocumentIndex } from './document-index/index.js';
import { VectorShape } from './vector-shape/index.js';
import { ShapeGroup } from './shape-group/index.js';
import { Chart } from './chart/index.js';
import { MathInline, MathBlock } from './math/index.js';
import { PassthroughBlock, PassthroughInline } from '@extensions/passthrough/index.js';
import { IndexEntry } from './index-entry/index.js';
import { TableOfContentsEntry } from './table-of-contents-entry/index.js';
import { CrossReference } from './cross-reference/index.js';
import { SequenceField } from './sequence-field/index.js';
import { DocumentStatField } from './document-stat-field/index.js';
import { FieldUpdate } from './field-update/index.js';
import { Citation } from './citation/index.js';
import { Bibliography } from './bibliography/index.js';
import { AuthorityEntry } from './authority-entry/index.js';
import { TableOfAuthorities } from './table-of-authorities/index.js';

// Marks extensions
import { TextStyle } from './text-style/text-style.js';
import { Bold } from './bold/index.js';
import { Italic } from './italic/index.js';
import { Underline } from './underline/index.js';
import { Highlight } from './highlight/index.js';
import { Strike } from './strike/index.js';
import { Link } from './link/index.js';
import { TrackInsert, TrackDelete, TrackFormat, TrackChanges, trackChangesHelpers } from './track-changes/index.js';
import { TextTransform } from './text-transform/index.js';

// Plugins
import { Placeholder } from './placeholder/index.js';
import { PopoverPlugin } from './popover-plugin/index.js';
import { LinkedStyles } from './linked-styles/linked-styles.js';
import { Search } from './search/index.js';
import { NodeResizer } from './noderesizer/index.js';
import { CustomSelection } from './custom-selection/index.js';
import { PermissionRanges } from './permission-ranges/index.js';
import { Protection } from './protection/index.js';
import { VerticalNavigation } from './vertical-navigation/index.js';
import { MixedBidiBackspace } from './mixed-bidi-backspace/index.js';

// Permissions
import { PermStart, PermStartBlock } from './perm-start/index.js';
import { PermEnd, PermEndBlock } from './perm-end/index.js';

// Helpers
import { Diffing } from './diffing/index.js';

const getRichTextExtensions = () => {
  return [
    Bold,
    Color,
    Document,
    FontFamily,
    FontSize,
    LetterSpacing,
    History,
    Heading,
    Italic,
    Link,
    Paragraph,
    TableOfContents,
    DocumentIndex,
    Strike,
    Text,
    TextAlign,
    TextStyle,
    Underline,
    Placeholder,
    PopoverPlugin,
    Mention,
    Highlight,
    FormatCommands,
    Table,
    TableRow,
    TableCell,
    TableHeader,
    FieldAnnotation,
    DropCursor,
    TrackInsert,
    TrackDelete,
    TrackFormat,
    AiPlugin,
    Image,
    NodeResizer,
    CustomSelection,
    MixedBidiBackspace,
    MathInline,
    MathBlock,
    PassthroughInline,
    PassthroughBlock,
  ];
};

const getStarterExtensions = () => {
  return [
    Bold,
    BlockNode,
    Color,
    CommentRangeStart,
    CommentRangeEnd,
    CommentReference,
    FootnoteReference,
    EndnoteReference,
    Document,
    FontFamily,
    FontSize,
    LetterSpacing,
    History,
    Heading,
    Italic,
    Link,
    Paragraph,
    LineBreak,
    HardBreak,
    Run,
    ContextMenu,
    Strike,
    TabNode,
    NoBreakHyphenNode,
    TableOfContents,
    TocPageNumber,
    DocumentIndex,
    Text,
    TextAlign,
    TextStyle,
    Underline,
    FormatCommands,
    CommentsPlugin,
    Gapcursor,
    Table,
    TableRow,
    TableCell,
    TableHeader,
    FieldAnnotation,
    DropCursor,
    Image,
    BookmarkStart,
    BookmarkEnd,
    SmartTag,
    Mention,
    Collaboration,
    CollaborationCursor,
    TrackChanges,
    TrackInsert,
    TrackDelete,
    TrackFormat,
    CommentsMark,
    Highlight,
    LinkedStyles,
    AiPlugin,
    AiMark,
    AiAnimationMark,
    AiLoaderNode,
    PageNumber,
    TotalPageCount,
    SectionPageCount,
    PageReference,
    IndexEntry,
    TableOfContentsEntry,
    CrossReference,
    SequenceField,
    DocumentStatField,
    FieldUpdate,
    Citation,
    Bibliography,
    AuthorityEntry,
    TableOfAuthorities,
    ShapeContainer,
    ShapeTextbox,
    ContentBlock,
    Search,
    StructuredContent,
    StructuredContentBlock,
    StructuredContentCommands,
    DocumentSection,
    DocumentPartObject,
    NodeResizer,
    CustomSelection,
    TextTransform,
    VectorShape,
    ShapeGroup,
    Chart,
    PermStart,
    PermEnd,
    PermStartBlock,
    PermEndBlock,
    PermissionRanges,
    Protection,
    VerticalNavigation,
    MixedBidiBackspace,
    MathInline,
    MathBlock,
    PassthroughInline,
    PassthroughBlock,
    Diffing,
  ];
};

export {
  History,
  Heading,
  Document,
  Text,
  Run,
  Paragraph,
  CommentRangeStart,
  CommentRangeEnd,
  CommentReference,
  FootnoteReference,
  EndnoteReference,
  TabNode,
  NoBreakHyphenNode,
  LineBreak,
  HardBreak,
  Bold,
  Italic,
  Underline,
  Highlight,
  Strike,
  Color,
  FontFamily,
  FontSize,
  LetterSpacing,
  TextAlign,
  TextStyle,
  FormatCommands,
  CommentsPlugin,
  Gapcursor,
  Table,
  TableRow,
  TableCell,
  TableHeader,
  DocumentIndex,
  TableOfContents,
  IndexEntry,
  TableOfContentsEntry,
  TocPageNumber,
  Placeholder,
  DropCursor,
  BlockNode,
  FieldAnnotation,
  fieldAnnotationHelpers,
  Image,
  BookmarkStart,
  BookmarkEnd,
  SmartTag,
  PopoverPlugin,
  Mention,
  Collaboration,
  CollaborationCursor,
  TrackChanges,
  TrackInsert,
  TrackDelete,
  TrackFormat,
  CommentsMark,
  trackChangesHelpers,
  getStarterExtensions,
  getRichTextExtensions,
  Diffing,
  AiMark,
  AiAnimationMark,
  AiLoaderNode,
  AiPlugin,
  Search,
  StructuredContent,
  StructuredContentBlock,
  StructuredContentCommands,
  DocumentSection,
  NodeResizer,
  CustomSelection,
  TextTransform,
  VectorShape,
  ShapeGroup,
  Chart,
  MathInline,
  MathBlock,
  PassthroughInline,
  PassthroughBlock,
  PermissionRanges,
  Protection,
  MixedBidiBackspace,
  CrossReference,
  SequenceField,
  DocumentStatField,
  FieldUpdate,
  Citation,
  Bibliography,
  AuthorityEntry,
  TableOfAuthorities,
};
